/* Brightivity version of StudioCore's OrgQueueContext (same exported shape: isOrgAdmin, pendingCount,
 * refreshPendingCount, myQueueVersion, bumpMyQueue). StudioCore listens to Supabase Realtime; Brightivity may only
 * talk to brightivity.dev, so it polls the school queue instead: every 30 s, on window focus, and after the
 * console approves/rejects (refreshPendingCount). */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { orgApi } from '../services/orgApi';

const OrgQueueContext = createContext(null);
const POLL_MS = 30000;

export function OrgQueueProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [myQueueVersion, setMyQueueVersion] = useState(0);
  const isOrgAdmin = !!user?.orgAdmin;

  const refreshPendingCount = useCallback(async () => {
    if (!user?.token || !user?.orgAdmin) return;
    try {
      const data = await orgApi.listQueue(user.token, 'pending');
      setPendingCount(data?.total ?? (data?.jobs || []).length ?? 0);
    } catch (err) {
      console.warn('[OrgQueue] pending count refetch failed:', err.message);
    }
  }, [user?.token, user?.orgAdmin]);

  useEffect(() => {
    if (!isAuthenticated) { setPendingCount(0); setMyQueueVersion(0); return undefined; }
    const tick = () => { refreshPendingCount(); setMyQueueVersion((v) => v + 1); };
    if (isOrgAdmin) refreshPendingCount();
    const t = setInterval(() => { if (document.visibilityState === 'visible') tick(); }, POLL_MS);
    window.addEventListener('focus', tick);
    return () => { clearInterval(t); window.removeEventListener('focus', tick); };
  }, [isAuthenticated, isOrgAdmin, refreshPendingCount]);

  const bumpMyQueue = useCallback(() => setMyQueueVersion((v) => v + 1), []);
  const value = { isOrgAdmin, pendingCount, refreshPendingCount, myQueueVersion, bumpMyQueue };
  return <OrgQueueContext.Provider value={value}>{children}</OrgQueueContext.Provider>;
}

export function useOrgQueue() {
  const context = useContext(OrgQueueContext);
  if (!context) throw new Error('useOrgQueue must be used within an OrgQueueProvider');
  return context;
}

export default OrgQueueContext;
